// src/pages/Letter.jsx - Enhanced with better layout and header separator
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tabs,
  Tab,
  Avatar,
  Switch,
  FormControlLabel,
  InputAdornment
} from '@mui/material';
import {
  PictureAsPdf as PdfIcon,
  WhatsApp as WhatsAppIcon,
  Save as SaveIcon,
  Print as PrintIcon,
  Share as ShareIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Preview as PreviewIcon,
  Download as DownloadIcon,
  ContentCopy as CopyIcon,
  Edit as EditIcon
} from '@mui/icons-material';
import { db } from '../firebase/config';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

const Letter = () => {
  const { isAdmin, userData, currentUser } = useAuth();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [customRecipient, setCustomRecipient] = useState('');
  const [useCustomRecipient, setUseCustomRecipient] = useState(false);
  const [letterData, setLetterData] = useState({
    title: '',
    subject: '',
    body: '',
    date: new Date().toLocaleDateString(),
    reference: '',
    signature: '',
    designation: '',
    recipientName: '',
    recipientAddress: '',
    recipientPhone: '',
    recipientEmail: ''
  });
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [generatedPdfUrl, setGeneratedPdfUrl] = useState(null);
  const [pdfBlob, setPdfBlob] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showWhatsAppDialog, setShowWhatsAppDialog] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  const letterRef = useRef();
  const fileInputRef = useRef();

  // Letter templates
  const letterTemplates = {
    appointment: {
      title: 'Appointment Letter',
      subject: 'Appointment to Ministry Position',
      body: `We are pleased to inform you of your appointment as [Position] in White Doves Ministry. Your dedication and commitment to the work of God have been recognized, and we believe you will serve faithfully in this capacity.

Your appointment takes effect from [Date]. Kindly report to [Location] on your first day of service.

May the Lord guide and strengthen you as you take up this new responsibility.`
    },
    transfer: {
      title: 'Transfer Letter',
      subject: 'Transfer to New Mission Field',
      body: `This is to inform you of your transfer from [Current Location] to [New Location]. This transfer is effective from [Date].

We trust that the Lord will use you mightly in your new assignment as we continue to spread the gospel.`
    },
    appreciation: {
      title: 'Appreciation Letter',
      subject: 'Appreciation for Faithful Service',
      body: `We write to express our heartfelt appreciation for your dedicated service to White Doves Ministry. Your commitment and sacrifice have not gone unnoticed.

May the Lord continue to bless and keep you as you serve in His vineyard.`
    },
    discipline: {
      title: 'Advisory Letter',
      subject: 'Advisory on Ministry Conduct',
      body: `This letter serves as an advisory regarding [Issue]. We encourage you to align with the ministry's core values and guidelines.

We believe in restoration and growth, and we are committed to walking with you through this process.`
    },
    custom: {
      title: '',
      subject: '',
body: ''
    }
  };

  const [selectedTemplate, setSelectedTemplate] = useState('custom');

  // Fetch users on mount
  useEffect(() => {
    if (isAdmin) {
      fetchUsers();
    }
  }, [isAdmin]);

  const fetchUsers = async () => {
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      const usersList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
    } catch (error) {
      console.error('Error fetching users:', error);
      showSnackbar('Failed to fetch users', 'error');
    }
  };

  const handleTemplateChange = (template) => {
    setSelectedTemplate(template);
    if (template !== 'custom') {
      setLetterData({
        ...letterData,
        title: letterTemplates[template].title,
        subject: letterTemplates[template].subject,
        body: letterTemplates[template].body
      });
    }
  };

  const handleInputChange = (field, value) => {
    setLetterData(prev => ({ ...prev, [field]: value }));
  };

  const getLetterHtml = () => {
    const ministryName = "White Doves Ministry";
    const ministryLogo = "https://res.cloudinary.com/dblffn98e/image/upload/v1771566469/rbonf3jdrbuf5cu2qvbr.png";
    const ministryAddress = "P.O. Box 12345, Nairobi, Kenya";
    const ministryPhone = "+254 7400 86845";
    const ministryEmail = "info@whitedovesministry@gmail";
    const  ministryWebsite = "whitedoskusda.top";
   
    
    // Determine recipient display
    let recipientDisplay = '';
    if (useCustomRecipient && customRecipient) {
      recipientDisplay = customRecipient;
    } else if (selectedUser) {
      recipientDisplay = `${selectedUser.fullName || selectedUser.firstName || 'Sir/Madam'}`;
      if (selectedUser.address) recipientDisplay += `<br />${selectedUser.address}`;
      if (selectedUser.phone) recipientDisplay += `<br />Phone: ${selectedUser.phone}`;
      if (selectedUser.email) recipientDisplay += `<br />Email: ${selectedUser.email}`;
    } else if (letterData.recipientName) {
      recipientDisplay = letterData.recipientName;
      if (letterData.recipientAddress) recipientDisplay += `<br />${letterData.recipientAddress}`;
      if (letterData.recipientPhone) recipientDisplay += `<br />Phone: ${letterData.recipientPhone}`;
      if (letterData.recipientEmail) recipientDisplay += `<br />Email: ${letterData.recipientEmail}`;
    } else {
      recipientDisplay = 'Sir/Madam';
    }
    
    return `
       <!DOCTYPE html>
    <html>
    <head>
      <meta charset="UTF-8">
      <title>${letterData.title || 'Letter'}</title>
      <style>
        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }
        
        body {
          font-family: 'Times New Roman', Times, serif;
          line-height: 1.5;
          color: #333;
          background: white;
        }
        
        .letter-wrapper {
          min-height: 100vh;
          display: flex;
          flex-direction: column;
          padding: 40px;
        }
        
        .letter-container {
          max-width: 800px;
          margin: 0 auto;
          background: white;
          flex: 1;
          display: flex;
          flex-direction: column;
        }
        
        /* Improved Header Design */
        .header {
          margin: -40px -40px 0 -40px;
          padding: 30px 40px;
          background: white;
          border-bottom: 2px solid #1034A6;
        }
        
        .header-content {
          display: flex;
          align-items: center;
          gap: 20px;
        }
        
        .logo-section {
          flex-shrink: 0;
        }
        
        .logo {
          width: 80px;
          height: 80px;
          object-fit: contain;
        }
        
        .ministry-details {
          flex: 1;
          text-align: center;
        }
        
        .ministry-name {
          font-size: 22px;
          font-weight: bold;
          color: #1034A6;
          letter-spacing: 1px;
          margin-bottom: 8px;
        }
        
        .ministry-address {
          font-size: 11px;
          color: #555;
          line-height: 1.4;
        }
        
        .ministry-contact {
          font-size: 11px;
          color: #555;
          margin-top: 5px;
        }
        
        .ministry-tagline {
          font-size: 11px;
          color: #888;
          font-style: italic;
          margin-top: 5px;
          padding-top: 5px;
          border-top: 1px solid #ddd;
        }
        
        /* Content area */
        .content {
          flex: 1;
          padding: 40px 40px 20px 40px;
        }
        
        .letter-ref {
          text-align: right;
          margin-bottom: 20px;
          font-size: 12px;
        }
        
        .date {
          text-align: right;
          margin-bottom: 30px;
        }
        
        .recipient {
          margin-bottom: 30px;
          line-height: 1.6;
        }
        
        .recipient-label {
          font-weight: bold;
        }
        
        .subject {
          font-weight: bold;
          margin: 20px 0;
          text-decoration: underline;
        }
        
        .body {
          text-align: justify;
          margin-bottom: 30px;
          white-space: pre-wrap;
          line-height: 1.6;
        }
        
        .signature {
          margin-top: 50px;
        }
        
        .signature-line {
          margin-top: 30px;
          width: 200px;
          border-top: 1px solid #333;
        }
        
        .signature-name {
          font-weight: bold;
          margin-top: 5px;
        }
        
        .designation {
          font-size: 12px;
          color: #666;
        }
        
        /* Footer */
        .footer {
          margin: 0 -40px -40px -40px;
          padding: 15px 40px;
          text-align: center;
          font-size: 10px;
          color: #999;
          border-top: 1px solid #eee;
          background: white;
        }
        
        /* Print styles */
        @media print {
          body {
            padding: 0;
            margin: 0;
          }
          .letter-wrapper {
            padding: 0;
          }
          .header {
            margin: 0 0 20px 0;
            padding: 20px;
          }
          .footer {
            margin: 20px 0 0 0;
            padding: 20px;
          }
          .content {
            padding: 20px;
          }
        }
      </style>
    </head>
    <body>
      <div class="letter-wrapper">
        <div class="letter-container">
          <div class="header">
            <div class="header-content">
              <div class="logo-section">
                <img src="${ministryLogo}" alt="Logo" class="logo" />
              </div>
              <div class="ministry-details">
                <div class="ministry-name">${ministryName}</div>
                <div class="ministry-address">
                  ${ministryAddress}
                </div>
                <div class="ministry-contact">
                  Tel: ${ministryPhone} | Email: ${ministryEmail} | Website: ${ministryWebsite}
                </div>
                <div class="ministry-tagline">
                  "I saw the Spirit come down from heaven as a dove and remain on him." John 1:32
                </div>
              </div>
            </div>
          </div>
          
          <div class="content">
            <div class="letter-ref">
              <strong>Ref:</strong> ${letterData.reference || `${ministryName.toUpperCase().replace(/\s/g, '')}/LET/${new Date().getFullYear()}/${Math.floor(Math.random() * 1000)}`}
            </div>
            
            <div class="date">
              ${new Date(letterData.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </div>
            
            <div class="recipient">
              <span class="recipient-label">To:</span><br />
              ${recipientDisplay}
            </div>
            
            <div class="subject">
              SUBJECT: ${letterData.subject || 'LETTER OF COMMUNICATION'}
            </div>
            
            <div class="body">
              ${letterData.body || 'No content provided.'}
            </div>
            
            <div class="signature">
              <p>Yours faithfully,</p>
              <div class="signature-line"></div>
              <div class="signature-name">${letterData.signature || userData?.fullName || 'Ministry Administrator'}</div>
              <div class="designation">${letterData.designation || 'For: White Doves Ministry'}</div>
            </div>
          </div>
          
          <div class="footer">
            
            White Doves Ministry - We are a family like never before and we mean it.
          </div>
        </div>
      </div>
    </body>
    </html>
  `;
};

  const generatePDF = async () => {
    if (!letterData.body) {
      showSnackbar('Please enter letter content', 'error');
      return;
    }

    setLoading(true);
    try {
      // Create a temporary container for rendering
      const container = document.createElement('div');
      container.innerHTML = getLetterHtml();
      container.style.position = 'fixed';
      container.style.left = '-9999px';
      container.style.top = '0';
      container.style.width = '800px';
      container.style.background = 'white';
      document.body.appendChild(container);
      
      // Wait for images to load
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Capture the container
      const canvas = await html2canvas(container, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff',
        windowWidth: container.scrollWidth,
        windowHeight: container.scrollHeight
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      // Calculate image dimensions to fit A4
      const imgWidth = 190; // mm (A4 width minus margins)
      const pageHeight = 277; // mm (A4 height minus margins)
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let position = 10; // Top margin
      
      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      
      // Add new page if content overflows
      let heightLeft = imgHeight - pageHeight;
      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
      
      // Save PDF
      pdf.save(`letter-${Date.now()}.pdf`);
      
      // Also convert to blob for WhatsApp
      const pdfBlob = pdf.output('blob');
      setPdfBlob(pdfBlob);
      const url = URL.createObjectURL(pdfBlob);
      setGeneratedPdfUrl(url);
      
      // Clean up
      document.body.removeChild(container);
      showSnackbar('PDF generated successfully!', 'success');
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      showSnackbar('Failed to generate PDF: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppSend = () => {
    if (!whatsappNumber) {
      showSnackbar('Please enter WhatsApp number', 'error');
      return;
    }

    if (!pdfBlob) {
      showSnackbar('Please generate PDF first', 'error');
      return;
    }

    // Format WhatsApp number
    let formattedNumber = whatsappNumber.replace(/\D/g, '');
    if (formattedNumber.startsWith('0')) {
      formattedNumber = '254' + formattedNumber.substring(1);
    } else if (!formattedNumber.startsWith('254')) {
      formattedNumber = '254' + formattedNumber;
    }

    // Create WhatsApp URL with message
    const recipientName = useCustomRecipient ? customRecipient : (selectedUser?.fullName || letterData.recipientName || 'Sir/Madam');
    const message = `Dear ${recipientName},\n\nPlease find attached letter from White Doves Ministry.\n\nTitle: ${letterData.title || 'Letter'}\nReference: ${letterData.reference || 'N/A'}\n\nGod bless you.`;
    
    const whatsappUrl = `https://wa.me/${formattedNumber}?text=${encodeURIComponent(message)}`;
    
    window.open(whatsappUrl, '_blank');
    
    showSnackbar('WhatsApp opened. Please manually attach the PDF file.', 'info');
    setShowWhatsAppDialog(false);
  };

  const downloadPDF = async () => {
    await generatePDF();
  };

  const printLetter = () => {
    const printWindow = window.open('', '_blank');
    printWindow.document.write(getLetterHtml());
    printWindow.document.close();
    printWindow.print();
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredUsers = users.filter(user =>
    user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phone?.includes(searchTerm)
  );

  if (!isAdmin) {
    return (
      <Box sx={{ p: 3, maxWidth: 600, mx: 'auto' }}>
        <Alert severity="error">
          Access Denied. Only administrators can access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1400, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#1034A6', fontWeight: 'bold', mb: 3 }}>
        <DescriptionIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Letter Management System
      </Typography>

      <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} sx={{ mb: 3 }}>
        <Tab icon={<DescriptionIcon />} label="Compose Letter" />
        <Tab icon={<PersonIcon />} label="Select Recipient" />
        <Tab icon={<PreviewIcon />} label="Preview" />
      </Tabs>

      {/* Compose Letter Tab */}
      {activeTab === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Letter Composition</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Letter Template</InputLabel>
                    <Select
                      value={selectedTemplate}
                      label="Letter Template"
                      onChange={(e) => handleTemplateChange(e.target.value)}
                    >
                      <MenuItem value="custom">Custom Letter</MenuItem>
                      <MenuItem value="appointment">Appointment Letter</MenuItem>
                      <MenuItem value="transfer">Transfer Letter</MenuItem>
                      <MenuItem value="appreciation">Appreciation Letter</MenuItem>
                      <MenuItem value="discipline">Advisory Letter</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Letter Title"
                    value={letterData.title}
                    onChange={(e) => handleInputChange('title', e.target.value)}
                    placeholder="e.g., Appointment Letter"
                  />

                  <TextField
                    fullWidth
                    label="Subject"
                    value={letterData.subject}
                    onChange={(e) => handleInputChange('subject', e.target.value)}
                    placeholder="Subject of the letter"
                  />

                  <TextField
                    fullWidth
                    label="Reference Number"
                    value={letterData.reference}
                    onChange={(e) => handleInputChange('reference', e.target.value)}
                    placeholder="e.g., WDM/LET/001/2024"
                    helperText="Leave blank for auto-generated reference"
                  />

                  <TextField
                    fullWidth
                    type="date"
                    label="Date"
                    value={letterData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />

                  <TextField
                    fullWidth
                    multiline
                    rows={12}
                    label="Letter Body"
                    value={letterData.body}
                    onChange={(e) => handleInputChange('body', e.target.value)}
                    placeholder="Type your letter content here..."
                  />

                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Signature Name"
                        value={letterData.signature}
                        onChange={(e) => handleInputChange('signature', e.target.value)}
                        placeholder="Your name"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Designation"
                        value={letterData.designation}
                        onChange={(e) => handleInputChange('designation', e.target.value)}
                        placeholder="e.g., Ministry Administrator"
                      />
                    </Grid>
                  </Grid>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Actions</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Stack spacing={2}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<PdfIcon />}
                    onClick={downloadPDF}
                    disabled={loading}
                    sx={{ bgcolor: '#d32f2f', '&:hover': { bgcolor: '#b71c1c' } }}
                  >
                    {loading ? <CircularProgress size={24} /> : 'Download PDF'}
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PrintIcon />}
                    onClick={printLetter}
                  >
                    Print Letter
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<WhatsAppIcon />}
                    onClick={() => setShowWhatsAppDialog(true)}
                    sx={{ color: '#25D366', borderColor: '#25D366' }}
                  >
                    Send via WhatsApp
                  </Button>

                  <Alert severity="info" sx={{ mt: 2 }}>
                    <strong>Tips:</strong>
                    <ul style={{ margin: '5px 0 0 15px' }}>
                      <li>Fill in all placeholders like [Position], [Date]</li>
                      <li>Select a recipient from the next tab</li>
                      <li>Preview the letter before sending</li>
                    </ul>
                  </Alert>
                </Stack>
              </CardContent>
            </Card>

            <Card sx={{ mt: 2 }}>
              <CardContent>
                <Typography variant="subtitle2" gutterBottom>Quick Insert Placeholders</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label="[Name]" onClick={() => handleInputChange('body', letterData.body + ' [Name]')} size="small" />
                  <Chip label="[Position]" onClick={() => handleInputChange('body', letterData.body + ' [Position]')} size="small" />
                  <Chip label="[Date]" onClick={() => handleInputChange('body', letterData.body + ' [Date]')} size="small" />
                  <Chip label="[Location]" onClick={() => handleInputChange('body', letterData.body + ' [Location]')} size="small" />
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Select Recipient Tab */}
      {activeTab === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={5}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Search & Select Recipient</Typography>
                <Divider sx={{ mb: 2 }} />
                
                <Box sx={{ mb: 2 }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={useCustomRecipient}
                        onChange={(e) => setUseCustomRecipient(e.target.checked)}
                      />
                    }
                    label="Use Custom Recipient"
                  />
                </Box>
                
                {useCustomRecipient ? (
                  <Stack spacing={2}>
                    <TextField
                      fullWidth
                      label="Recipient Name"
                      value={customRecipient}
                      onChange={(e) => setCustomRecipient(e.target.value)}
                      placeholder="Enter recipient name"
                    />
                    <TextField
                      fullWidth
                      label="Address (Optional)"
                      value={letterData.recipientAddress}
                      onChange={(e) => handleInputChange('recipientAddress', e.target.value)}
                      placeholder="Enter address"
                    />
                    <TextField
                      fullWidth
                      label="Phone (Optional)"
                      value={letterData.recipientPhone}
                      onChange={(e) => handleInputChange('recipientPhone', e.target.value)}
                      placeholder="Enter phone number"
                    />
                    <TextField
                      fullWidth
                      label="Email (Optional)"
                      value={letterData.recipientEmail}
                      onChange={(e) => handleInputChange('recipientEmail', e.target.value)}
                      placeholder="Enter email address"
                    />
                  </Stack>
                ) : (
                  <>
                    <TextField
                      fullWidth
                      label="Search by name, email or phone"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      sx={{ mb: 2 }}
                    />
                    
                    <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                      <Stack spacing={1}>
                        {filteredUsers.map(user => (
                          <Paper
                            key={user.id}
                            sx={{
                              p: 2,
                              cursor: 'pointer',
                              bgcolor: selectedUser?.id === user.id ? '#e3f2fd' : 'white',
                              '&:hover': { bgcolor: '#f5f5f5' }
                            }}
                            onClick={() => {
                              setSelectedUser(user);
                              setUseCustomRecipient(false);
                            }}
                          >
                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                              <Box>
                                <Typography fontWeight="bold">{user.fullName || user.firstName}</Typography>
                                <Typography variant="caption" display="block" color="textSecondary">
                                  <EmailIcon fontSize="inherit" /> {user.email}
                                </Typography>
                                <Typography variant="caption" display="block" color="textSecondary">
                                  <PhoneIcon fontSize="inherit" /> {user.phone || 'No phone'}
                                </Typography>
                              </Box>
                              {selectedUser?.id === user.id && <CheckCircleIcon color="primary" />}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  </>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={7}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Selected Recipient</Typography>
                <Divider sx={{ mb: 2 }} />
                
                {useCustomRecipient ? (
                  <Stack spacing={1}>
                    <Typography variant="body1"><strong>Name:</strong> {customRecipient || 'Not specified'}</Typography>
                    {letterData.recipientAddress && <Typography variant="body1"><strong>Address:</strong> {letterData.recipientAddress}</Typography>}
                    {letterData.recipientPhone && <Typography variant="body1"><strong>Phone:</strong> {letterData.recipientPhone}</Typography>}
                    {letterData.recipientEmail && <Typography variant="body1"><strong>Email:</strong> {letterData.recipientEmail}</Typography>}
                    
                    <Button
                      variant="outlined"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => {
                        setWhatsappNumber(letterData.recipientPhone || '');
                        setShowWhatsAppDialog(true);
                      }}
                      sx={{ mt: 2 }}
                      disabled={!letterData.recipientPhone}
                    >
                      Send Letter via WhatsApp
                    </Button>
                  </Stack>
                ) : selectedUser ? (
                  <Stack spacing={1}>
                    <Typography variant="body1"><strong>Name:</strong> {selectedUser.fullName || selectedUser.firstName}</Typography>
                    <Typography variant="body1"><strong>Email:</strong> {selectedUser.email}</Typography>
                    <Typography variant="body1"><strong>Phone:</strong> {selectedUser.phone || 'Not provided'}</Typography>
                    <Typography variant="body1"><strong>Year:</strong> {selectedUser.yearName || 'Not specified'}</Typography>
                    
                    <Button
                      variant="outlined"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => {
                        setWhatsappNumber(selectedUser.phone || '');
                        setShowWhatsAppDialog(true);
                      }}
                      sx={{ mt: 2 }}
                      disabled={!selectedUser.phone}
                    >
                      Send Letter via WhatsApp
                    </Button>
                  </Stack>
                ) : (
                  <Alert severity="info">
                    No recipient selected. The letter will be generated with "Sir/Madam" as the default recipient.
                  </Alert>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Preview Tab */}
      {activeTab === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Letter Preview</Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Paper
              sx={{
                p: 4,
                bgcolor: '#fafafa',
                minHeight: 600,
                overflow: 'auto',
                border: '1px solid #ddd'
              }}
              dangerouslySetInnerHTML={{ __html: getLetterHtml() }}
            />
            
            <Box sx={{ mt: 2, display: 'flex', gap: 2 }}>
              <Button variant="contained" startIcon={<PdfIcon />} onClick={downloadPDF}>
                Download PDF
              </Button>
              <Button variant="outlined" startIcon={<PrintIcon />} onClick={printLetter}>
                Print
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* WhatsApp Dialog */}
      <Dialog open={showWhatsAppDialog} onClose={() => setShowWhatsAppDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <WhatsAppIcon sx={{ mr: 1, color: '#25D366' }} />
          Send via WhatsApp
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label="WhatsApp Number"
              value={whatsappNumber}
              onChange={(e) => setWhatsappNumber(e.target.value)}
              placeholder="e.g., 254712345678"
              helperText="Enter number with country code (e.g., 254712345678)"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <WhatsAppIcon />
                  </InputAdornment>
                ),
              }}
            />
            
            <Alert severity="info">
              <strong>Instructions:</strong>
              <ol style={{ margin: '8px 0 0 16px' }}>
                <li>First, generate and download the PDF</li>
                <li>Click "Open WhatsApp" below</li>
                <li>Manually attach the downloaded PDF file</li>
                <li>Send the message</li>
              </ol>
            </Alert>
            
            {generatedPdfUrl && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<DownloadIcon />}
                href={generatedPdfUrl}
                download="letter.pdf"
              >
                Download PDF (to attach)
              </Button>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWhatsAppDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            startIcon={<WhatsAppIcon />}
            onClick={handleWhatsAppSend}
            sx={{ bgcolor: '#25D366', '&:hover': { bgcolor: '#128C7E' } }}
          >
            Open WhatsApp
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default Letter;
